import type {
  AttributeFilters,
  SnapshotId,
  TableRequestBody,
  TableResponseBody,
} from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import classNames from 'classnames';
import { Button } from 'components/Button/Button';
import { FormikSelectField } from 'components/FormikSelectField/FormikSelectField';
import type { ArrayHelpers, FormikProps } from 'formik';

import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import { type JSX, useMemo, useState } from 'react';
import { nestedFormikName } from 'utils/formik/nestedFormikName';
import { useAutocompleteOptions } from 'utils/hooks/useAutocompleteOptions';
import { useEffectOnChange } from 'utils/react/useEffectOnChange';
import { type ApiHook, useApi } from 'utils/request/useApi';
import { useGlobalStateContextSelectors } from '../../../GlobalState/GlobalStateModel';
import type { MulticastFormValues } from './MulticastFormValues';
import type { UnicastFormValues } from './UnicastFormValues';

interface Props<TFormikValues> {
  arrayHelpers: ArrayHelpers;
  formik: FormikProps<TFormikValues>;
  index: number;
}

type SnValue = {
  sn: string;
  hostname: string;
};

type LoadedData = {
  label: string;
  value: SnValue;
};

type FirstHopsRowData = {
  sn: string;
  hostname: string;
  interfaces: string[];
};

const SEARCH_DEBOUNCE_DURATION = 600;

const makeLoadHostnames =
  (
    api: ApiHook,
    snapshotId: SnapshotId,
    attributeFilters: AttributeFilters,
    additionalData: Map<string, string[]>,
    setAdditionalData: (data: Map<string, string[]>) => void,
  ) =>
  async (
    filter: string,
    page: number,
  ): Promise<{ data: LoadedData[]; count: number }> => {
    const response = await api.request<
      TableResponseBody<FirstHopsRowData>,
      TableRequestBody<FirstHopsRowData>
    >({
      url: '/tables/addressing/path-lookup-first-hops',
      method: 'POST',
      data: {
        attributeFilters,
        columns: ['sn', 'hostname', 'interfaces'],
        filters: filter
          ? {
              hostname: ['reg', `(?i)${filter}`],
            }
          : {},
        pagination: {
          limit: 20,
          start: page * 20,
        },
        snapshot: snapshotId,
      },
    });

    const options = response.data.map(({ sn, hostname }) => ({
      label: hostname,
      value: { sn, hostname },
    }));
    const newAdditionalData = response.data.reduce(
      (acc, { sn, interfaces }) => {
        acc.set(sn, interfaces);

        return acc;
      },
      page === 0 ? new Map() : additionalData,
    );

    setAdditionalData(newAdditionalData);

    return { data: options, count: response._meta.count };
  };

export const GraphFirstHopEntryPoint = <
  TFormikValues extends MulticastFormValues | UnicastFormValues,
>(
  props: Props<TFormikValues>,
): JSX.Element => {
  const [additionalData, setAdditinalData] = useState<Map<string, string[]>>(
    new Map(),
  );
  const [lastSelectedOption, setLastSelectedOption] = useState<SnValue | null>(
    null,
  );

  const api = useApi();

  const ctxProps = useGraphContextSelectors({
    snapshotId: prop('snapshotId'),
  });

  const globalCtxProps = useGlobalStateContextSelectors({
    attributeFilters: prop('attributeFilters'),
  });

  const autoCompleteOptions = useAutocompleteOptions<LoadedData>({
    load: makeLoadHostnames(
      api,
      ctxProps.snapshotId,
      globalCtxProps.attributeFilters,
      additionalData,
      setAdditinalData,
    ),
    debounceInterval: SEARCH_DEBOUNCE_DURATION,
  });

  useEffectOnChange(() => {
    autoCompleteOptions.reload();
  }, [globalCtxProps.attributeFilters]);

  const handleAutoCompleteInputValueChange = useStableCallback(
    (event: string) => {
      autoCompleteOptions.handleInputValueChange(event);
    },
  );

  const { touched: snTouched } = props.formik.getFieldMeta(
    `entryPoints.${props.index}.host`,
  );
  const { touched: ifaceTouched } = props.formik.getFieldMeta(
    `entryPoints.${props.index}.iface`,
  );

  const fieldError =
    snTouched && ifaceTouched
      ? (props.formik.errors.entryPoints?.[props.index] as string)
      : undefined;

  const handleChange = (value: SnValue | null) => {
    setLastSelectedOption(value);
    props.formik.setFieldValue(`entryPoints.${props.index}.iface`, null);
  };

  const snOptions = useMemo(() => {
    const selectedValue = props.formik.values.entryPoints[props.index]?.host;

    if (
      selectedValue &&
      !autoCompleteOptions.loadedData.some(
        ({ value }) =>
          value.hostname === selectedValue.hostname &&
          value.sn === selectedValue.sn,
      )
    ) {
      return [
        // Have to keep current option in list, otherwise input value will be resetted
        {
          label: selectedValue.hostname,
          value: { hostname: selectedValue.hostname, sn: selectedValue.sn },
        },
        ...autoCompleteOptions.loadedData,
      ];
    }
    return autoCompleteOptions.loadedData;
  }, [
    autoCompleteOptions.loadedData,
    props.formik.values.entryPoints,
    props.index,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: update only with option change
  const ifaceOptions = useMemo(() => {
    const selectedValue = props.formik.values.entryPoints[props.index]?.iface;
    const interfaces = lastSelectedOption
      ? additionalData.get(lastSelectedOption.sn)
      : [];
    const options = interfaces
      ? interfaces?.map((value) => ({ label: value, value }))
      : [];

    if (
      !selectedValue ||
      options.some(({ value }) => value === selectedValue)
    ) {
      return options;
    }
    return [
      {
        label: selectedValue,
        value: selectedValue,
      },
      ...options,
    ];
  }, [lastSelectedOption]);

  return (
    <div className={classNames(fieldError && 'formik-error-scroll-anchor')}>
      <div className='flex'>
        <div className='relative flex'>
          <div className='w-32'>
            <FormikSelectField
              formik={props.formik}
              name={nestedFormikName(`entryPoints.${props.index}.host`)}
              options={snOptions}
              inputValue={autoCompleteOptions.inputValue}
              onInputValueChange={handleAutoCompleteInputValueChange}
              onChange={handleChange}
              getOptionValue={(value) =>
                value ? `s/${value.hostname}` : 'null'
              }
              nonClearable
              size='small'
              stretchOnOpenDropdown
              loading={autoCompleteOptions.loadingData}
              searchable
              onScrollBottom={autoCompleteOptions.loadNextPage}
            />
          </div>
          <div className='ml-2.5'>
            <FormikSelectField
              formik={props.formik}
              disabled={!props.formik.values.entryPoints[props.index]?.host}
              name={nestedFormikName(`entryPoints.${props.index}.iface`)}
              options={ifaceOptions}
              searchable
              nonClearable
              size='small'
            />
          </div>
        </div>
        <Button
          variant='secondary'
          accent='negative'
          className='mx-2.5'
          onClick={(e) => {
            e.preventDefault();
            props.arrayHelpers.remove(props.index);
          }}
          icon='fa-trash-can'
          disabled={props.formik.values.entryPoints.length < 2}
          type='button'
        />
        <Button
          variant='secondary'
          onClick={(e) => {
            e.preventDefault();
            props.arrayHelpers.push({ host: null, iface: null });
          }}
          icon='fa-plus'
          disabled={props.formik.values.entryPoints.length > 2}
          type='button'
        />
      </div>
    </div>
  );
};
